import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { X, ChevronLeft } from 'lucide-react'

// Import step components
import { ConsentStep } from './steps/ConsentStep'
import { IncomeStep } from './steps/IncomeStep'
import { FixedExpensesStep } from './steps/FixedExpensesStep'
import { VariableExpensesStep } from './steps/VariableExpensesStep'
import { SavingsGoalsStep } from './steps/SavingsGoalsStep'
import { YearlyBudgetStep } from './steps/YearlyBudgetStep'
import { MiscellaneousStep } from './steps/MiscellaneousStep'
import { ResultsStep } from './steps/ResultsStep'

interface SmartBudgetAssistantProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

const ASSISTANT_STEPS = [
  { id: 'consent', title: 'Welcome', canSkip: false },
  { id: 'income', title: 'Income', canSkip: false },
  { id: 'fixed', title: 'Fixed Expenses', canSkip: true },
  { id: 'variable', title: 'Variable Expenses', canSkip: true },
  { id: 'savings', title: 'Savings Goals', canSkip: true },
  { id: 'yearly', title: 'Yearly Budget', canSkip: true },
  { id: 'misc', title: 'Miscellaneous', canSkip: true },
  { id: 'results', title: 'Your Budget Plan', canSkip: false }
]

export function SmartBudgetAssistant({ isOpen, onClose, onComplete }: SmartBudgetAssistantProps) {
  const { t } = useTranslation()
  const [currentStep, setCurrentStep] = useState(0)

  const handleStepNext = () => {
    const nextStep = currentStep + 1
    if (nextStep >= ASSISTANT_STEPS.length) {
      // Complete the assistant
      onComplete()
    } else {
      setCurrentStep(nextStep)
    }
  }

  const handleStepBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepSkip = () => {
    handleStepNext()
  }

    const renderCurrentStep = () => {
    const stepId = ASSISTANT_STEPS[currentStep]?.id
    
    switch (stepId) {
      case 'consent':
        return <ConsentStep onNext={handleStepNext} />
      case 'income':
        return <IncomeStep onNext={handleStepNext} onSkip={handleStepSkip} />
      case 'fixed':
        return <FixedExpensesStep onNext={handleStepNext} onSkip={handleStepSkip} />
      case 'variable':
        return <VariableExpensesStep onNext={handleStepNext} onSkip={handleStepSkip} />
      case 'savings':
        return <SavingsGoalsStep onNext={handleStepNext} onSkip={handleStepSkip} />
      case 'yearly':
        return <YearlyBudgetStep onNext={handleStepNext} onSkip={handleStepSkip} />
      case 'misc':
        return <MiscellaneousStep onNext={handleStepNext} onSkip={handleStepSkip} />
      case 'results':
        return <ResultsStep onComplete={onComplete} />
      default:
        return (
          <div className="text-center">
            <h2 className="text-xl font-semibold">Step not found</h2>
            <Button onClick={onComplete} className="mt-4">
              Complete
            </Button>
          </div>
        )
    }
  }

  const currentStepConfig = ASSISTANT_STEPS[currentStep]
  const progress = ((currentStep + 1) / ASSISTANT_STEPS.length) * 100

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                {currentStepConfig?.title || 'Smart Budget Assistant'}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                Step {currentStep + 1} of {ASSISTANT_STEPS.length} - Create a personalized budget plan
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <Progress value={progress} className="h-2" />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          {renderCurrentStep()}
        </div>
        
        {/* Navigation Footer */}
        {currentStep > 0 && currentStepConfig?.id !== 'results' && (
          <div className="border-t p-4 flex justify-between">
            <Button
              variant="outline"
              onClick={handleStepBack}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="text-sm text-muted-foreground">
              {progress.toFixed(0)}% Complete
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
} 